import { purchasedGoodsRoutes } from '@app/purchased-goods/purchased-goods-routes';
import {
  CompanyReportCategory,
  CompanyReportCategoryStatusStatusEnum,
} from '@shared/api/types';
import Alert from '@shared/components/alert';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import SelectFormField from '@shared/components/form/select-form-field';
import { Button } from '@shared/components/ui/button';
import { Card, CardContent, CardHeader } from '@shared/components/ui/card';
import { generatePath, useNavigate } from 'react-router-dom';
import { reasonOptions } from '../utils';

export function ScopeReviewRow({
  icon,
  name,
  description,
  slug,
  status,
  hasActivityData,
  isSupported,
  hasUnreviewedData,
}: {
  icon: React.ReactNode;
  name: string;
  description: string;
  slug: CompanyReportCategory['slug'];
  status: CompanyReportCategoryStatusStatusEnum;
  hasActivityData: boolean;
  isSupported?: boolean;
  hasUnreviewedData?: boolean;
}) {
  const options = isSupported
    ? reasonOptions.filter((option) => option.value !== 'not_supported')
    : reasonOptions;

  const categoryOmitted = !hasActivityData || !isSupported;
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 py-3">
        <div>{icon}</div>
        <span className="ml-2 text-md">{name}</span>
        <div className="ml-auto flex items-baseline">
          <span className="flex items-center">
            {status !== CompanyReportCategoryStatusStatusEnum.complete &&
              hasActivityData && (
                <>
                  <Icons.loader2 className="mr-1 text-orange-300" size={14} />
                  <Text variant="subtle">Data added</Text>
                </>
              )}
            {status === CompanyReportCategoryStatusStatusEnum.complete && (
              <>
                <Icons.check className="mr-1 text-secondary-400" size={14} />
                <Text variant="subtle">Complete</Text>
              </>
            )}
          </span>
        </div>
      </CardHeader>
      {hasUnreviewedData && slug === 'food' && (
        <CardContent className="space-y-2 p-4 pt-0">
          <Alert variant="warning" className="self-center py-1">
            <div className="flex items-center justify-between ">
              <div className="flex items-center space-x-2">
                <Icons.warning className=" text-warning-700" size={16} />
                <Text>Includes items that needs review for accuracy</Text>
              </div>

              <Button
                variant="link"
                onClick={() =>
                  navigate(
                    generatePath(
                      purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_REVIEW
                    )
                  )
                }
                className="ml-auto text-warning-700"
              >
                Review
              </Button>
            </div>
          </Alert>
        </CardContent>
      )}
      {categoryOmitted && (
        <CardContent className="space-y-2 p-4 pt-0">
          <Text className="w-full text-left" variant="subtle">
            {description}
          </Text>
          <div className="flex w-full space-x-4 ">
            <Text className="mt-[10px] text-nowrap text-right">
              Reason for excluding category:
            </Text>
            <SelectFormField
              name={`${slug}.status`}
              className="flex-1 text-left"
              options={options}
              includeErrorMessage
            />
          </div>
        </CardContent>
      )}
    </Card>
  );
}
